import type { DuplicateWine } from "@corklog/shared";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

/**
 * What the catalogue already has of the wine being registered.
 *
 * Two claims, and keeping them apart is the whole of it. **The same wine in
 * the same year is a duplicate**, and the right answer is a new lot against
 * the number that already exists — a wine is catalogued once and keeps its
 * number forever, so a second entry splits one wine's history in two.
 * **The same wine in another year is not**, and a new entry is correct: 2012
 * and 2015 are different wines, and acting on that match would file the
 * bottles behind the wrong number. So the first carries a link and an
 * instruction and the second is a sentence.
 *
 * Nothing here refuses the registration. The person holding the bottle knows
 * things the catalogue does not, and this is the same bargain the picker's
 * *did you mean* strikes: detected here, decided there.
 */
export function DuplicateWines({
  matches,
  cellarId,
}: {
  matches: readonly DuplicateWine[];
  cellarId: string;
}) {
  const { t } = useTranslation();
  const duplicates = matches.filter((match) => match.kind === "same-vintage");
  const others = matches.filter((match) => match.kind === "other-vintage");
  if (matches.length === 0) return null;

  return (
    <div className="border-accent col-span-full rounded-sm border p-4">
      {duplicates.length > 0 && (
        <>
          <p className="font-display text-base">{t("wines.duplicate.heading")}</p>
          <ul className="mt-2 space-y-1">
            {duplicates.map((match) => (
              <WineRow key={match.wine.id} match={match} cellarId={cellarId}>
                <span className="text-muted">
                  {" — "}
                  {match.wine.owned === 0
                    ? t("wines.duplicate.none")
                    : t("wines.duplicate.owned", { count: match.wine.owned })}
                </span>
              </WineRow>
            ))}
          </ul>
          <p className="text-muted mt-2 max-w-prose text-sm">
            {t("wines.duplicate.offer")}
          </p>
        </>
      )}

      {others.length > 0 && (
        <div
          className={duplicates.length > 0 ? "border-line mt-3 border-t pt-3" : undefined}
        >
          <p className="text-muted max-w-prose text-sm">{t("wines.duplicate.others")}</p>
          <ul className="mt-1 space-y-1">
            {others.map((match) => (
              <WineRow key={match.wine.id} match={match} cellarId={cellarId} />
            ))}
          </ul>
          <p className="text-muted mt-2 max-w-prose text-sm">
            {t("wines.duplicate.othersHint")}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * One catalogued wine, in the shape the list gives it: the number in a column
 * of its own, muted and in the display face, and the wine beside it.
 *
 * The number is set apart by position rather than by a word, and this is what
 * the first look at the page on a phone changed. Written into the sentence —
 * *1 G.D. Vajra Barolo Bricco delle Viole 2021* — it read as a quantity rather
 * than as the number printed on the label, which is the one thing about a row
 * like this that has to be unmistakable. The column costs nothing and the list
 * had already solved it.
 */
function WineRow({
  match,
  cellarId,
  children,
}: {
  match: DuplicateWine;
  cellarId: string;
  children?: React.ReactNode;
}) {
  const { wine } = match;
  return (
    <li className="grid grid-cols-[2.25rem_1fr] gap-x-3 text-sm">
      <span className="font-display text-muted text-right">{wine.number}</span>
      <span className="min-w-0">
        <Link
          to={`/kallare/${cellarId}/viner/${wine.id}`}
          className="text-accent underline-offset-4 hover:underline"
        >
          {wine.producer} {wine.name} {wine.vintage ?? "NV"}
        </Link>
        {children}
      </span>
    </li>
  );
}
